import React, { useState } from 'react';
import styled from 'styled-components';
import { MainContent, ContentWrapper } from '../components/Containers';
import SectionTitle from '../components/SectionTitle';
import {
  Paragraph,
  Button,
  TableWrapper,
  Table,
  THead,
  Th,
  TableRow,
  Td,
  CellText,
  InputLabel,
  MenuButton,
  MenuItem,
} from '@increasecard/typed-components';
import { useUser } from '@increasecard/increase-core';
import { usePayAuthorizations } from '../hooks/usePayAuthorizations';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../components/LoadingIndicator';
import { format } from 'date-fns';
import InviteUserModal from '../components/InviteUserModal';
import InlineAlert from '../components/InlineAlert';
import { ApiErrorMessage } from '../components/ApiErrorMessage';
import { DeleteUserModal } from '../components/DeleteUserModal';
import { useUsersAndInvitations } from '../hooks/useUsersAndInvitations'; // Importa tu nuevo hook

const StyledInputLabel = styled(InputLabel)`
  font-weight: bold;
  color: ${({ status }) =>
    status === 'failed' || status === 'pending'
      ? 'var(--color-coralAlert-regular)'
      : status === 'sent'
        ? `#D29249`
        : 'var(--color-gray-grayBold)'};
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PERMISSIONS = {
  MANAGE_USERS_INVITE: 'manage_users_invite',
  MANAGE_USERS_ROLES: 'manage_users_roles',
  MANAGE_USERS_DELETE: 'manage_users_delete',
};

export default function AggregatorAccountUsers() {
  const { t } = useTranslation('settingsUsers');
  const activeUser = useUser();
  const { userHasAuthorization } = usePayAuthorizations();

  // Estados puramente de UI (Modales)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editData, setEditData] = useState(null);

  // Consumo del Hook
  const {
    invitations,
    users,
    loading,
    apiError,
    successMessage,
    setSuccessMessage,
    handleResend,
    handleRemove,
    handleDeleteUserSuccess,
    handleInviteSuccess,
  } = useUsersAndInvitations();

  // Loader simplificado gracias al estado 'loading' del hook
  if (activeUser === null || loading || !invitations || !users) {
    return <LoadingIndicator />;
  }

  const canInviteUsers = userHasAuthorization(PERMISSIONS.MANAGE_USERS_INVITE);
  const canManageRoles = userHasAuthorization(PERMISSIONS.MANAGE_USERS_ROLES);
  const canDeleteUsers = userHasAuthorization(PERMISSIONS.MANAGE_USERS_DELETE);

  return (
    <ContentWrapper>
      <MainContent>
        <HeaderContainer>
          <SectionTitle style={{ margin: 0 }}>{t('title')}</SectionTitle>
          {canInviteUsers && (
            <Button
              onClick={() => {
                setEditData(null);
                setIsInviteModalOpen(true);
              }}
            >
              Invitar usuario
            </Button>
          )}
        </HeaderContainer>

        <Paragraph style={{ marginBottom: 20 }}>
          Administra los accesos de tus usuarios al producto Pay.
        </Paragraph>

        {successMessage && (
          <InlineAlert
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
            style={{ margin: '15px 0' }}
            variant="info"
          />
        )}
        <ApiErrorMessage error={apiError} marginTop={15} />

        <TableWrapper>
          <Table style={{ fontSize: '13px' }} zebra>
            <THead>
              <tr>
                <Th>{t('user')}</Th>
                <Th>{t('email')}</Th>
                <Th>{t('status')}</Th>
                <Th>{t('role')}</Th>
                <Th rightAligned />
              </tr>
            </THead>
            <tbody>
              {invitations.map((invitation) => (
                <TableRow double key={invitation.id}>
                  <Td>
                    <CellText weight="bold">
                      {invitation.first_name} {invitation.last_name}
                    </CellText>
                  </Td>
                  <Td>{invitation.email}</Td>
                  <Td>
                    <StyledInputLabel status={invitation.status}>
                      {invitation.status === 'sent' ? 'Invitación pendiente' : invitation.status}
                    </StyledInputLabel>
                  </Td>
                  <Td>
                    <CellText>{t(`roles.${invitation.role}`) || invitation.role}</CellText>
                  </Td>
                  <Td rightAligned>
                    <MenuButton>
                      {({ toggleShowMenu }) => (
                        <>
                          <MenuItem
                            disabled={!canInviteUsers}
                            onClick={() => {
                              toggleShowMenu();
                              handleResend(invitation);
                            }}
                          >
                            Reenviar invitación
                          </MenuItem>
                          <MenuItem
                            disabled={!canInviteUsers}
                            onClick={() => {
                              toggleShowMenu();
                              handleRemove(invitation);
                            }}
                            variant="alert"
                          >
                            Eliminar invitación
                          </MenuItem>
                        </>
                      )}
                    </MenuButton>
                  </Td>
                </TableRow>
              ))}
              {users.map((user) => {
                const payRole = user.authorizations.find((a) => a.product_name === 'pay')?.role;
                return (
                  <TableRow double key={user.id}>
                    <Td>
                      <CellText weight="bold">
                        {user.firstName} {user.lastName}
                      </CellText>
                    </Td>
                    <Td>{user.email}</Td>
                    <Td>
                      <StyledInputLabel status="accepted">Invitación aceptada</StyledInputLabel>
                      <CellText>{format(new Date(user.createdAt), 'dd/MM/yyyy')}</CellText>
                    </Td>
                    <Td>
                      <CellText>
                        {user.owner ? t('roles.owner') : t(`roles.${payRole}`) || payRole || '-'}
                      </CellText>
                    </Td>
                    <Td rightAligned>
                      {!user.owner && user.id !== activeUser.id && (
                        <MenuButton>
                          {({ toggleShowMenu }) => (
                            <>
                              <MenuItem
                                disabled={!canManageRoles}
                                onClick={() => {
                                  setEditData(user);
                                  setIsInviteModalOpen(true);
                                  toggleShowMenu();
                                }}
                              >
                                Editar
                              </MenuItem>
                              <MenuItem
                                disabled={!canDeleteUsers}
                                onClick={() => {
                                  setUserToDelete(user);
                                  toggleShowMenu();
                                }}
                                variant="alert"
                              >
                                Eliminar usuario
                              </MenuItem>
                            </>
                          )}
                        </MenuButton>
                      )}
                    </Td>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>

        <InviteUserModal
          initialData={editData}
          onClose={() => {
            setIsInviteModalOpen(false);
            setEditData(null);
          }}
          onSuccess={(email) => {
            handleInviteSuccess(email, !!editData);
            setIsInviteModalOpen(false);
          }}
          visible={isInviteModalOpen}
        />

        <DeleteUserModal
          onCancel={() => setUserToDelete(null)}
          onDelete={() => {
            setUserToDelete(null);
            handleDeleteUserSuccess();
          }}
          user={userToDelete}
          visible={!!userToDelete}
        />
      </MainContent>
    </ContentWrapper>
  );
}
